/** FINISAJELE: ce se calcă și ce se atinge — pardoseala și tencuiala.
 *
 *  DOUĂ CATALOAGE, NU UNUL. O pardoseală și o zugrăveală nu se pot alege din
 *  aceeași listă: prima se vede DE SUS, pe plan, ca o suprafață mare cu rost și
 *  scară proprie; a doua se vede doar în machetă, pe o fâșie verticală de
 *  câțiva metri pătrați, și acolo contează culoarea, nu rostul. Puse într-o
 *  singură listă, jumătate din alegeri ar fi fost nepotrivite oriunde s-ar fi
 *  făcut — cine a pus vreodată parchet pe perete sau vopsea lavabilă pe jos.
 *
 *  CE ÎNSEAMNĂ FIECARE CIFRĂ:
 *
 *    * `fond` e culoarea plină de sub hașură. Hașura desenează DOAR rosturile,
 *      ca în `plan/arhMaterial`, fiindcă un `<pattern>` cu fond propriu se vede
 *      «cusut» la marginea camerei, unde tile-ul e tăiat.
 *    * `modul` e latura reală a elementului, în METRI: o scândură de parchet are
 *      0,19 m, o dală mare 0,60, o gresie de baie 0,20. Desenul e în metri, deci
 *      hașura se îndesește singură când omul mărește — fără niciun prag și fără
 *      nicio rotiță de «scara texturii».
 *    * `pbr` e rolul din `public/arh3d/materiale/manifest.json`, adică ce
 *      textură CC0 (ambientCG) îmbracă suprafața în machetă. Lipsă = machetă pe
 *      culoare plină, care e răspunsul cinstit pentru o vopsea: o vopsea chiar
 *      nu are textură la scara la care se privește o machetă.
 *
 *  IMPLICITUL E PE FELUL CAMEREI și e același tabel de convenții pe care îl are
 *  de mult `plan/arhMaterial::PARDOSEALA` — lemn unde se doarme, gresie unde e
 *  apă, beton la tehnic. Diferența e ce se întâmplă după: acolo tabelul ERA
 *  răspunsul, aici e doar punctul din care cineva schimbă. De-aia documentul
 *  scrie numai ce s-a schimbat (v. `FinisajDoc`), iar lista de finisaje a unui
 *  modul nemodificat e goală.
 */
import { t } from "../../../i18n";
import type { Cheie } from "../../../i18n";

/** O intrare din oricare din cele două cataloage. */
export type Finisaj = {
  cheie: string;
  nume: string;
  /** Familia, pentru grupele din paletă. */
  grupa: string;
  fond: string;
  /** Culoarea rostului sau a desenului de deasupra fondului. */
  rost: string;
  /** Latura reală a elementului, în metri. 0 = suprafață fără rost. */
  modul: number;
  /** Cum se desenează rostul — v. `hasuraFinisajului`. */
  desen: "dale" | "scanduri" | "brad" | "caramida" | "lamele" | "pete" | "neted";
  /** Rolul din manifestul de materiale CC0, pentru machetă. */
  pbr?: string;
};

// ---------------------------------------------------------------- pardoseli ---

/** Numele finisajului, în limba ecranului — v. `arhMobilierPlan::numePiesa`.
 *  Catalogul ține numele românesc fiindcă el e cheia tehnică; ecranul ia
 *  `finisaj.<cheie>`. */
export const numeFinisaj = (cheie: string): string =>
  (finisajul(cheie) ? t(`finisaj.${cheie}` as Cheie) : cheie);

export const PARDOSELI: Finisaj[] = [
  // PARCHETUL IMPLICIT AL CAMERELOR — dungi, nu scânduri cu capete decalate.
  //
  // Desenul e cel din planșa de referință (`layout.pdf`, «The Perrie»): linii
  // paralele continue, fără rostul de cap la jumătate de scândură. Așa se
  // desenează un parchet pe un plan de vânzare — rostul lung spune direcția
  // pozării, iar capetele n-au ce căuta acolo, la scara planșei nu se văd.
  // `scanduri` rămâne pentru cine vrea desenul întreg; implicitul e ăsta.
  { cheie: "parchet_standard", nume: "Parchet standard", grupa: "Lemn",
    fond: "#e0cdae", rost: "#bfa285", modul: 0.19, desen: "lamele", pbr: "parchet" },
  { cheie: "parchet_stejar", nume: "Parchet stejar", grupa: "Lemn",
    fond: "#dcc4a0", rost: "#c0a179", modul: 0.19, desen: "scanduri", pbr: "parchet" },
  { cheie: "parchet_nuc", nume: "Parchet nuc", grupa: "Lemn",
    fond: "#b4885d", rost: "#8f6742", modul: 0.19, desen: "scanduri", pbr: "parchet" },
  { cheie: "parchet_brad", nume: "Parchet în brad", grupa: "Lemn",
    fond: "#d6bb96", rost: "#b3946c", modul: 0.34, desen: "brad", pbr: "parchet" },
  { cheie: "punte", nume: "Punte lemn (exterior)", grupa: "Lemn",
    fond: "#c9b499", rost: "#a48f73", modul: 0.14, desen: "scanduri", pbr: "punte" },

  { cheie: "dale_mari", nume: "Dale 60 × 60", grupa: "Piatră și ceramică",
    fond: "#e9e1d5", rost: "#d3c8b6", modul: 0.6, desen: "dale", pbr: "marmura" },
  { cheie: "marmura", nume: "Marmură", grupa: "Piatră și ceramică",
    fond: "#ece8e0", rost: "#d6d0c4", modul: 0.8, desen: "dale", pbr: "marmura" },
  { cheie: "gresie_baie", nume: "Gresie 20 × 20", grupa: "Piatră și ceramică",
    fond: "#e2ded6", rost: "#cbc4b8", modul: 0.2, desen: "dale", pbr: "gresie_baie" },
  { cheie: "gresie_bucatarie", nume: "Gresie 30 × 30", grupa: "Piatră și ceramică",
    fond: "#e4e0d6", rost: "#ccc5b7", modul: 0.3, desen: "dale", pbr: "gresie_bucatarie" },

  { cheie: "beton", nume: "Beton șlefuit", grupa: "Turnate și textile",
    fond: "#d6cfc4", rost: "#c2baac", modul: 0, desen: "pete", pbr: "beton" },
  { cheie: "mocheta", nume: "Mochetă", grupa: "Turnate și textile",
    fond: "#cfc5b8", rost: "#bcb0a0", modul: 0, desen: "pete", pbr: "covor" },
];

// ------------------------------------------------------------ pereți (finisaj) ---

export const PERETI: Finisaj[] = [
  { cheie: "vopsea_alba", nume: "Vopsea albă", grupa: "Zugrăveli",
    fond: "#f6f4f0", rost: "#e7e3dc", modul: 0, desen: "neted" },
  { cheie: "vopsea_calda", nume: "Vopsea caldă", grupa: "Zugrăveli",
    fond: "#f0e7d8", rost: "#dfd3bf", modul: 0, desen: "neted" },
  { cheie: "vopsea_gri", nume: "Vopsea gri", grupa: "Zugrăveli",
    fond: "#dfdcd7", rost: "#cbc7c0", modul: 0, desen: "neted" },
  { cheie: "vopsea_salvie", nume: "Vopsea salvie", grupa: "Zugrăveli",
    fond: "#d8dfd3", rost: "#c1cbba", modul: 0, desen: "neted" },
  { cheie: "vopsea_indigo", nume: "Vopsea indigo", grupa: "Zugrăveli",
    fond: "#c3ccd8", rost: "#a8b4c4", modul: 0, desen: "neted" },

  { cheie: "faianta", nume: "Faianță 20 × 20", grupa: "Placaje",
    fond: "#e8ecee", rost: "#d0d6da", modul: 0.2, desen: "dale", pbr: "gresie_baie" },
  { cheie: "faianta_mare", nume: "Faianță 30 × 60", grupa: "Placaje",
    fond: "#e6e3dd", rost: "#cfcac1", modul: 0.3, desen: "dale", pbr: "gresie_bucatarie" },
  { cheie: "caramida", nume: "Cărămidă aparentă", grupa: "Placaje",
    fond: "#c28a6c", rost: "#a5715a", modul: 0.25, desen: "caramida" },
  { cheie: "lambriu", nume: "Lambriu lemn", grupa: "Placaje",
    fond: "#d9bd97", rost: "#b89b76", modul: 0.12, desen: "lamele", pbr: "lemn" },
];

/** Căutare pe cheie, în amândouă cataloagele. */
const TOATE = new Map([...PARDOSELI, ...PERETI].map((f) => [f.cheie, f]));
export const finisajul = (cheie?: string | null): Finisaj | null =>
  (cheie ? TOATE.get(cheie) ?? null : null);

// ------------------------------------------------------------------ implicitul ---

/** PARDOSEALA IMPLICITĂ A FIECĂREI FUNCȚIUNI.
 *
 *  Aceleași perechi ca în `plan/arhMaterial::PARDOSEALA`, și din aceleași
 *  motive scrise acolo: holul locuinței ia dale ca livingul, fiindcă zona de zi
 *  curge fără prag prin el; balconul ia PUNTE și nu lemn de interior, fiindcă
 *  scândura lată e singurul lucru care spune «afară»; casa scării și liftul iau
 *  beton fiindcă nucleul nu e locuință. Un fel nespus ia dale mari — e ce se
 *  desenează când nu se știe. */
export const PARDOSEALA_IMPLICITA: Record<string, string> = {
  living: "parchet_standard",
  hall: "dale_mari", hol: "dale_mari", sas: "dale_mari",
  bedroom: "parchet_standard", dormitor: "parchet_standard",
  // `room` e camera căreia nu i s-a spus încă ce e (v. `feluri::FEL_IMPLICIT`):
  // e o încăpere de locuit, deci ia parchet ca dormitorul, nu dalele holului.
  room: "parchet_standard",
  kitchen: "gresie_bucatarie",
  bath: "gresie_baie", bath2: "gresie_baie", wc: "gresie_baie",
  shower: "gresie_baie",
  // DEBARAUA IA PARCHETUL LIVINGULUI, nu betonul.
  //
  // Cerut de arhitect (19.10.2026): «Debara sa aibă aceleasi finisaje ca
  // livingul (parchet standard) si din import si sa se modifice si toate pe
  // care le avem deja acum». Betonul era al spațiilor tehnice — ghenă, casa
  // scării —, iar debaraua din apartament nu e un spațiu tehnic: e un dulap
  // mare, se intră din living și se calcă pe același parchet.
  //
  // SCHIMBAREA PRINDE ȘI DOCUMENTELE VECHI, fiindcă ăsta e IMPLICITUL: o
  // cameră care nu-și scrie finisajul îl ia de-aici la fiecare desenare, iar
  // `pictaFinisaj` nici nu scrie rândul când alegerea e chiar implicitul.
  // Deci nu e nevoie de nicio migrare — nici pentru cele importate, nici
  // pentru cele desenate.
  store: "parchet_standard",
  ghena: "beton", tech: "beton",
  scara: "beton", lift: "beton", desfumare: "beton",
  balcony: "punte",
};

/** FINISAJUL IMPLICIT DE PERETE.
 *
 *  Faianță unde e apă, vopsea caldă unde se stă, alb unde se trece. Nu e gust:
 *  o baie fără placaj nu se poate spăla, iar un coridor comun se zugrăvește alb
 *  fiindcă el nu e al nimănui. */
export const PERETE_IMPLICIT: Record<string, string> = {
  living: "vopsea_calda",
  hall: "vopsea_alba", hol: "vopsea_alba", sas: "vopsea_alba",
  bedroom: "vopsea_calda", dormitor: "vopsea_calda", room: "vopsea_calda",
  kitchen: "faianta_mare",
  bath: "faianta", bath2: "faianta", wc: "faianta", shower: "faianta",
  store: "vopsea_alba", ghena: "vopsea_alba", tech: "vopsea_gri",
  scara: "vopsea_gri", lift: "vopsea_gri", desfumare: "vopsea_gri",
  balcony: "vopsea_alba",
};

export const pardoseala = (kind?: string | null): string =>
  (kind && PARDOSEALA_IMPLICITA[kind]) || "dale_mari";
export const pereteleCamerei = (kind?: string | null): string =>
  (kind && PERETE_IMPLICIT[kind]) || "vopsea_alba";

// ------------------------------------------------------------------- desenul ---

/** Id-ul pattern-ului unui finisaj. Prefixat `des-` și unic pe CHEIE, nu pe
 *  instanță: pe pagină pot sta mai multe `<svg>` deodată, fiecare cu `<defs>`-ul
 *  lui, iar `url(#…)` se leagă de primul găsit. Două lucruri diferite cu același
 *  nume ar fi o greșeală; același lucru de două ori nu e. */
export const patternul = (cheie: string, plansa = false) =>
  plansa ? `des-plansa-${cheie}` : `des-${cheie}`;

/** TUȘUL ROSTULUI PE PLANȘA TEHNICĂ. Planșa e alb-negru: pardoseala se
 *  citește doar din DESENUL rostului — scânduri, dale, grăunte —, nu din
 *  culoare. Un gri DESCHIS, cerut anume «mai light, să nu încurce citirea de
 *  rute și etichete»: rostul e textură de fond, nu linie de zid. */
export const ROST_PLANSA = "#b4b4b4";

/** Rostul unui finisaj, ca `<pattern>` în metri.
 *
 *  Fără fond — v. nota din capul fișierului: fondul plin se desenează dedesubt,
 *  altfel tile-ul tăiat la marginea camerei se vede cusut. */
function Rost({ f, plansa = false }: { f: Finisaj; plansa?: boolean }) {
  const m = f.modul || 1;
  // Pe planșă, în gri de tuș; în design, în culoarea finisajului.
  const culoare = plansa ? ROST_PLANSA : f.rost;
  const id = patternul(f.cheie, plansa);
  // Rostul e o LINIE, nu o bandă: la 1,2% din modul, o dală de 60 cm îl are de
  // 7 mm și o gresie de 20 cm de 2,4 — chiar proporția reală, deci desenul nu se
  // îngroașă la finisajele mărunte. Era 2%, adică un rost de 12 mm la dala mare:
  // o bandă, nu o linie, și ea tăia desenul de deasupra tocmai la finisajele cu
  // caroiaj drept.
  //
  // PE PLANȘĂ, ÎNSĂ, GROSIMEA E ÎN PIXELI. Rostul real de 4 mm al unei gresii
  // are 0,16 px la 39 px/m, iar în gri deschis pe alb nu se vede deloc — «jumă-
  // tate din modelele de pardoseală nu funcționează». Pe planșă rostul e semn
  // de textură, nu măsură, deci ține 0,7 px la orice mărire, ca hașura zidului.
  const l = plansa
    ? { stroke: culoare, strokeWidth: 0.7, fill: "none",
        vectorEffect: "non-scaling-stroke" as const }
    : { stroke: culoare, strokeWidth: m * 0.012, fill: "none" };
  if (f.desen === "neted") return null;
  if (f.desen === "pete") {
    // Beton și mochetă n-au rost: au GRĂUNTE. Trei puncte răsfirate pe un tile
    // de 12 cm dau la mărire un fond viu și, de departe, o culoare plină. Pe
    // planșă punctul e un segment de zero lungime cu capete rotunde, gros de
    // 1,6 px — un cerc de 8 mm ar fi și el sub un pixel.
    return (
      <pattern id={id} width={0.12} height={0.12}
               patternUnits="userSpaceOnUse">
        {[[0.02, 0.03], [0.07, 0.09], [0.1, 0.04]].map(([x, y], i) => (
          plansa
            ? <path key={i} d={`M${x} ${y}h0.0001`} stroke={culoare} strokeWidth={1.6}
                    strokeLinecap="round" vectorEffect="non-scaling-stroke" />
            : <circle key={i} cx={x} cy={y} r={0.008} fill={culoare} opacity={0.75} />
        ))}
      </pattern>
    );
  }
  if (f.desen === "scanduri") {
    // Scânduri: rostul lung e continuu, capetele sunt decalate la jumătate — cum
    // se pozează un parchet, și singurul lucru care-l deosebește de o dală
    // dreptunghiulară.
    const L = f.modul * 6;
    return (
      <pattern id={id} width={L} height={f.modul * 2}
               patternUnits="userSpaceOnUse">
        <path d={`M0 0H${L}M0 ${f.modul}H${L}`} {...l} />
        <path d={`M${L / 2} 0V${f.modul}M0 ${f.modul}V${f.modul * 2}`} {...l} />
      </pattern>
    );
  }
  if (f.desen === "brad") {
    // În brad: două lamele la 45°, în V. E desenul care face un parchet de
    // apartament vechi să se citească din prima ca atare.
    const a = f.modul;
    return (
      <pattern id={id} width={a} height={a}
               patternUnits="userSpaceOnUse">
        <path d={`M0 ${a / 2}L${a / 2} 0L${a} ${a / 2}L${a / 2} ${a}Z`} {...l} />
        <path d={`M0 ${a / 2}L${a} ${a / 2}`} {...l} />
      </pattern>
    );
  }
  if (f.desen === "caramida") {
    const h = f.modul * 0.26;
    return (
      <pattern id={id} width={f.modul} height={h * 2}
               patternUnits="userSpaceOnUse">
        <path d={`M0 0H${f.modul}M0 ${h}H${f.modul}`} {...l} />
        <path d={`M${f.modul / 2} 0V${h}M0 ${h}V${h * 2}`} {...l} />
      </pattern>
    );
  }
  if (f.desen === "lamele") {
    return (
      <pattern id={id} width={f.modul} height={f.modul}
               patternUnits="userSpaceOnUse">
        <path d={`M0 0V${f.modul}`} {...l} />
      </pattern>
    );
  }
  // dale: un caroiaj curat, pe latura lor reală
  return (
    <pattern id={id} width={f.modul} height={f.modul}
             patternUnits="userSpaceOnUse">
      <path d={`M0 0H${f.modul}M0 0V${f.modul}`} {...l} />
    </pattern>
  );
}

/** Toate rosturile, emise o singură dată în `<defs>`-ul pânzei. */
export function FinisajeDefs() {
  return (
    <>
      {[...PARDOSELI, ...PERETI].map((f) => <Rost key={f.cheie} f={f} />)}
      {/* Aceleași rosturi, în tuș gri, pentru planșa tehnică alb-negru. */}
      {PARDOSELI.map((f) => <Rost key={`p-${f.cheie}`} f={f} plansa />)}
    </>
  );
}

/** SUB CÂT NU SE MAI DESENEAZĂ ROSTUL, în pixeli pe metru.
 *
 *  Aceeași socoteală ca `arhMaterial::PRAG_HASURA`, dar pe modulul FIECĂRUI
 *  finisaj și nu pe cel mai mare dintre ele: o gresie de 20 cm dispare într-o
 *  ceață gri mult înaintea unei dale de 60. Sub prag rămâne fondul plin, deci
 *  finisajul tot se deosebește după culoare. Un finisaj fără modul (vopsea,
 *  beton) n-are prag: n-are ce să se înghesuie. */
const MIN_PX_ROST = 5;
export const rostVizibil = (f: Finisaj, pxPerM: number): boolean =>
  f.desen !== "neted" && (f.modul === 0 ? pxPerM >= 14
                                        : f.modul * pxPerM >= MIN_PX_ROST);
